import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';
import { DEMO_PROTOCOL_DEVIATIONS } from '../api/demoData';

// ---------------------------------------------------------------------------
// Glossary entries for SOA terms
// ---------------------------------------------------------------------------
const SOA_GLOSSARY: GlossaryEntry[] = [
  { term: 'SOA', definition: 'Schedule of Assessments \u2014 protocol-defined table listing every procedure, the visits at which it is required, and the allowable time windows.' },
  { term: 'Visit Window', definition: 'The acceptable time range around a scheduled visit within which a procedure may still be performed without constituting a deviation.' },
  { term: 'Protocol Deviation', definition: 'Any departure from the IRB-approved protocol. Classified as Minor, Major, or Violation depending on impact on subject safety or data integrity.' },
  { term: 'Recording Delay', definition: 'Elapsed time between when a clinical event occurs (effectiveTimestamp) and when it is entered into the system (internalKnowledgeTimestamp). ALCOA requires contemporaneous recording.' },
  { term: 'IP Dispensing', definition: 'Investigational Product dispensing \u2014 the controlled distribution of study drug to subjects per protocol schedule.' },
  { term: 'PK Sample', definition: 'Pharmacokinetic blood sample drawn at protocol-specified time points to measure drug concentration.' },
  { term: 'PD Sample', definition: 'Pharmacodynamic blood sample measuring biological response to the investigational product.' },
  { term: 'DLT Window', definition: 'Dose Limiting Toxicity observation window \u2014 the protocol-defined period (typically 28 days) during which DLTs are assessed before dose escalation.' },
  { term: '3+3 Design', definition: 'Classical dose escalation design enrolling 3 subjects per cohort, expanding to 6 if a DLT is observed, to determine the MTD.' },
  { term: 'Conmeds', definition: 'Concomitant Medications \u2014 all other drugs a subject is taking during the trial, recorded for safety and interaction analysis.' },
  { term: 'ALCOA', definition: 'Attributable, Legible, Contemporaneous, Original, Accurate \u2014 FDA/ICH data integrity standard.' },
  { term: 'ICH E6(R3)', definition: 'Good Clinical Practice guideline requiring risk-based monitoring, ALCOA data governance, and validated audit systems.' },
];

// ---------------------------------------------------------------------------
// SOA procedure definitions (14 rows)
// ---------------------------------------------------------------------------
interface SOAProcedure {
  name: string;
  requiredVisits: number[];
  window: string;
  responsible: string;
}

const SOA_PROCEDURES: SOAProcedure[] = [
  { name: 'Informed Consent', requiredVisits: [1], window: 'Before any study procedure', responsible: 'Site PI' },
  { name: 'Medical History', requiredVisits: [1], window: 'Screening only', responsible: 'Site PI' },
  { name: 'Physical Exam', requiredVisits: [1, 4, 8, 12], window: '\u00b12 days', responsible: 'Site PI' },
  { name: 'Vital Signs', requiredVisits: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], window: '\u00b130 min of scheduled time', responsible: 'Site Nurse' },
  { name: 'ECG', requiredVisits: [1, 4, 8, 12], window: '\u00b11 day', responsible: 'Site Nurse' },
  { name: 'Blood PK', requiredVisits: [2, 3, 4, 5, 6, 8, 10, 12], window: '\u00b115 min of scheduled time', responsible: 'Site Lab' },
  { name: 'Blood PD', requiredVisits: [2, 4, 8, 12], window: '\u00b130 min', responsible: 'Site Lab' },
  { name: 'CBC/Lab', requiredVisits: [1, 2, 4, 6, 8, 10, 12], window: '\u00b11 day', responsible: 'Central Lab' },
  { name: 'Urine', requiredVisits: [1, 4, 8, 12], window: '\u00b11 day', responsible: 'Central Lab' },
  { name: 'AE Assessment', requiredVisits: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], window: 'Each visit', responsible: 'Site PI' },
  { name: 'Conmeds', requiredVisits: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], window: 'Each visit', responsible: 'Site Nurse' },
  { name: 'IP Dispensing', requiredVisits: [2, 4, 6, 8, 10], window: 'Per dosing schedule', responsible: 'Pharmacist' },
  { name: 'SAE Review', requiredVisits: [4, 8, 12], window: '\u00b11 day of visit', responsible: 'Sponsor Medical Monitor' },
  { name: 'Dose Escalation Vote', requiredVisits: [4, 8, 12], window: 'After DLT window closes', responsible: 'SRC Committee' },
];

const VISIT_LABELS = Array.from({ length: 12 }, (_, i) => `V${i + 1}`);

// ---------------------------------------------------------------------------
// Simulated completion map — which procedure+visit combos are done
// Key: "procedureIndex-visitNumber"
// ---------------------------------------------------------------------------
const COMPLETED_SET = new Set<string>([
  // Informed Consent V1
  '0-1',
  // Medical History V1
  '1-1',
  // Physical Exam V1, V4, V8
  '2-1', '2-4', '2-8',
  // Vital Signs V1-V10
  '3-1', '3-2', '3-3', '3-4', '3-5', '3-6', '3-7', '3-8', '3-9', '3-10',
  // ECG V1, V4, V8
  '4-1', '4-4', '4-8',
  // Blood PK V2-V6, V8, V10
  '5-2', '5-3', '5-4', '5-5', '5-6', '5-8', '5-10',
  // Blood PD V2, V4, V8
  '6-2', '6-4', '6-8',
  // CBC/Lab V1, V2, V4, V6, V8, V10
  '7-1', '7-2', '7-4', '7-6', '7-8', '7-10',
  // Urine V1, V4, V8
  '8-1', '8-4', '8-8',
  // AE Assessment V1-V10
  '9-1', '9-2', '9-3', '9-4', '9-5', '9-6', '9-7', '9-8', '9-9', '9-10',
  // Conmeds V1-V10
  '10-1', '10-2', '10-3', '10-4', '10-5', '10-6', '10-7', '10-8', '10-9', '10-10',
  // IP Dispensing V2, V4, V6, V8
  '11-2', '11-4', '11-6', '11-8',
  // SAE Review V4, V8
  '12-4', '12-8',
  // Dose Escalation Vote V4, V8
  '13-4', '13-8',
]);

// Missed = required but NOT in COMPLETED_SET and visit <= 10 (current progress)
// Computed dynamically in computeStats() rather than as a static set

// Outside window (completed but late)
const OUTSIDE_WINDOW_SET = new Set<string>([
  '3-5',   // Vital Signs V5 late
  '5-4',   // Blood PK V4 late
  '7-6',   // CBC/Lab V6 late
  '9-7',   // AE Assessment V7 late
]);

// ---------------------------------------------------------------------------
// Summary stats computation
// ---------------------------------------------------------------------------
function computeStats() {
  let totalRequired = 0;
  let completedOnTime = 0;
  let missed = 0;
  let outsideWindow = 0;

  SOA_PROCEDURES.forEach((proc, pIdx) => {
    proc.requiredVisits.forEach((v) => {
      if (v > 10) return; // visits 11-12 not yet reached
      totalRequired++;
      const key = `${pIdx}-${v}`;
      if (COMPLETED_SET.has(key)) {
        if (OUTSIDE_WINDOW_SET.has(key)) {
          outsideWindow++;
        } else {
          completedOnTime++;
        }
      } else {
        missed++;
      }
    });
  });

  const complianceRate = totalRequired > 0 ? Math.round(((completedOnTime + outsideWindow) / totalRequired) * 100) : 0;
  return { complianceRate, completedOnTime, totalRequired, missed, outsideWindow, deviations: DEMO_PROTOCOL_DEVIATIONS.length };
}

// ---------------------------------------------------------------------------
// Deviation rules
// ---------------------------------------------------------------------------
interface DeviationRule {
  rule: string;
  severity: 'Minor' | 'Major' | 'Violation';
  description: string;
}

const DEVIATION_RULES: DeviationRule[] = [
  { rule: 'DEV-001', severity: 'Minor', description: 'Vital signs recorded >30 min but <2h outside scheduled window' },
  { rule: 'DEV-002', severity: 'Major', description: 'PK sample collected >15 min outside protocol-specified time point' },
  { rule: 'DEV-003', severity: 'Major', description: 'Missed required visit procedure without documented justification' },
  { rule: 'DEV-004', severity: 'Violation', description: 'IP dispensed to ineligible subject or at wrong dose level' },
  { rule: 'DEV-005', severity: 'Minor', description: 'AE assessment documented >24h after visit but within 48h' },
  { rule: 'DEV-006', severity: 'Major', description: 'ECG not performed at required visit (V1, V4, V8, V12)' },
  { rule: 'DEV-007', severity: 'Violation', description: 'Informed consent not obtained before first study procedure' },
  { rule: 'DEV-008', severity: 'Minor', description: 'Concomitant medication update recorded >1 visit late' },
  { rule: 'DEV-009', severity: 'Major', description: 'SAE review not completed within 1 day of scheduled visit' },
  { rule: 'DEV-010', severity: 'Violation', description: 'Dose escalation vote conducted without complete DLT window data' },
];

const SEVERITY_STYLES: Record<string, { bg: string; color: string }> = {
  Minor: { bg: '#22543d', color: '#48bb78' },
  Major: { bg: '#744210', color: '#ecc94b' },
  Violation: { bg: '#742a2a', color: '#f56565' },
};

// ---------------------------------------------------------------------------
// Recording delay thresholds (FEATURE 2)
// ---------------------------------------------------------------------------
interface ThresholdRow {
  eventType: string;
  green: string;
  amber: string;
  red: string;
  regulatory: string;
}

const THRESHOLD_ROWS: ThresholdRow[] = [
  { eventType: 'Vital Signs', green: '\u226430 min', amber: '30 min \u2013 2h', red: '>2h', regulatory: 'ICH E6(R3) \u2014 contemporaneous' },
  { eventType: 'ECG', green: '\u22641h', amber: '1h \u2013 4h', red: '>4h', regulatory: 'ICH E6(R3) \u2014 contemporaneous' },
  { eventType: 'Blood PK', green: '\u226415 min', amber: '15 min \u2013 1h', red: '>1h', regulatory: '21 CFR 312 \u2014 PK integrity' },
  { eventType: 'Blood PD', green: '\u226430 min', amber: '30 min \u2013 2h', red: '>2h', regulatory: '21 CFR 312 \u2014 PD integrity' },
  { eventType: 'Lab Results', green: '\u226424h', amber: '24h \u2013 72h', red: '>72h', regulatory: 'ICH E6(R3) \u2014 timely recording' },
  { eventType: 'AE Report', green: '\u226424h', amber: '24h \u2013 48h', red: '>48h', regulatory: '21 CFR 312.32 \u2014 AE reporting' },
  { eventType: 'SAE Report', green: '\u22641h', amber: '1h \u2013 4h', red: '>4h', regulatory: '21 CFR 312.32 \u2014 7/15 day clock' },
  { eventType: 'General Data', green: '\u226472h', amber: '72h \u2013 5d', red: '>5d', regulatory: 'UCLA SOP \u2014 3 business days' },
  { eventType: 'IP Dispensing', green: '\u226430 min', amber: '30 min \u2013 2h', red: '>2h', regulatory: 'ICH E6(R3) Annex 1 \u2014 IP accountability' },
  { eventType: 'Consent', green: '\u226415 min', amber: '15 min \u2013 1h', red: '>1h', regulatory: '21 CFR 50 \u2014 informed consent' },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function ProtocolSetup() {
  const stats = computeStats();
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const navigate = useNavigate();

  return (
    <div style={styles.container}>
      <Glossary entries={SOA_GLOSSARY} />

      {/* Page Title */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h1 style={styles.title}>Protocol Setup &amp; Schedule of Assessments</h1>
      </div>
      <ComplianceBadges badges={['ICH E6(R3)', 'ALCOA', '21 CFR 312', 'ICH E6(R3) Annex 1', '21 CFR 312.32']} />

      {/* Upload Protocol CTA */}
      <div style={{
        backgroundColor: '#1a365d', border: '2px solid #4299e1', borderRadius: 10,
        padding: '20px 28px', marginTop: 16, marginBottom: 20,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' as const,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>
            Import a Protocol PDF
          </div>
          <div style={{ fontSize: 13, color: '#a0aec0', lineHeight: 1.5 }}>
            Upload any clinical protocol PDF to auto-extract the SOA grid, visit schedule, procedures, and CPT mapping.
          </div>
        </div>
        <button
          onClick={() => navigate('/budget?tab=import')}
          style={{
            padding: '12px 32px', borderRadius: 8, border: 'none', cursor: 'pointer',
            backgroundColor: '#4299e1', color: '#fff', fontSize: 15, fontWeight: 700,
            fontFamily: 'inherit', whiteSpace: 'nowrap' as const, flexShrink: 0,
            transition: 'background-color 0.15s',
          }}
        >
          Upload Protocol
        </button>
      </div>

      {/* ================================================================ */}
      {/* SECTION A — Trial Configuration Card                            */}
      {/* ================================================================ */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Trial Configuration</h2>
        <div style={styles.configGrid}>
          <ConfigItem label="Trial ID" value="DEMO-001" />
          <ConfigItem label="Trial Name" value="Phase 1 Oncology \u2014 D3RI Demo" />
          <ConfigItem label="Sponsor" value="D3RI Inc." />
          <ConfigItem label="Phase" value="Phase 1" />
          <ConfigItem label="Design" value="3+3 dose escalation" />
          <ConfigItem label="Total Visits" value="12" />
          <ConfigItem label="Observation Period" value="28 days per cohort" />
          <ConfigItem label="Protocol Version" value="v2.1" />
          <ConfigItem label="Amendment 1 Date" value="2/1/2026" />
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION B — Schedule of Assessments Grid                        */}
      {/* ================================================================ */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Schedule of Assessments (SOA)</h2>

        {/* Summary stats row */}
        <div style={styles.statsRow}>
          <StatBadge label="Protocol Compliance" value={`${stats.complianceRate}%`} color="#48bb78" />
          <StatBadge label="Completed On Time" value={`${stats.completedOnTime}/${stats.totalRequired}`} color="#4299e1" />
          <StatBadge label="Missed" value={String(stats.missed)} color="#f56565" />
          <StatBadge label="Outside Window" value={String(stats.outsideWindow)} color="#ecc94b" />
          <StatBadge label="Auto-detected Deviations" value={String(stats.deviations)} color="#ed8936" />
        </div>

        {/* SOA table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, position: 'sticky', left: 0, backgroundColor: '#1a202c', zIndex: 2, minWidth: 170 }}>Procedure</th>
                {VISIT_LABELS.map((vl) => (
                  <th key={vl} style={{ ...styles.th, textAlign: 'center', minWidth: 48 }}>{vl}</th>
                ))}
                <th style={{ ...styles.th, minWidth: 160 }}>Window</th>
                <th style={{ ...styles.th, minWidth: 160 }}>Responsible</th>
              </tr>
            </thead>
            <tbody>
              {SOA_PROCEDURES.map((proc, pIdx) => (
                <tr key={proc.name}>
                  <td style={{ ...styles.td, position: 'sticky', left: 0, backgroundColor: '#1a202c', zIndex: 1, fontWeight: 600, color: '#e2e8f0' }}>
                    {proc.name}
                  </td>
                  {VISIT_LABELS.map((_, vIdx) => {
                    const visitNum = vIdx + 1;
                    const isRequired = proc.requiredVisits.includes(visitNum);
                    const key = `${pIdx}-${visitNum}`;
                    const isCompleted = COMPLETED_SET.has(key);
                    const isMissed = isRequired && !isCompleted && visitNum <= 10;
                    const isOutsideWindow = OUTSIDE_WINDOW_SET.has(key);

                    let cellContent: React.ReactNode;
                    let cellColor: string;

                    if (!isRequired) {
                      cellContent = '\u25CB';
                      cellColor = '#4a5568';
                    } else if (isCompleted && isOutsideWindow) {
                      cellContent = '\u25CF';
                      cellColor = '#ecc94b';
                    } else if (isCompleted) {
                      cellContent = '\u2713';
                      cellColor = '#48bb78';
                    } else if (isMissed) {
                      cellContent = '\u2717';
                      cellColor = '#f56565';
                    } else {
                      // Required but not yet due (V11, V12)
                      cellContent = '\u25CF';
                      cellColor = '#4299e1';
                    }

                    return (
                      <td key={vIdx} style={{ ...styles.td, textAlign: 'center' }}>
                        <span style={{ color: cellColor, fontSize: 16, fontWeight: 700 }}>{cellContent}</span>
                      </td>
                    );
                  })}
                  <td style={{ ...styles.td, color: '#a0aec0', fontSize: 12 }}>{proc.window}</td>
                  <td style={{ ...styles.td, color: '#a0aec0', fontSize: 12 }}>{proc.responsible}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
          <LegendItem symbol="\u2713" color="#48bb78" label="Completed on time" />
          <LegendItem symbol="\u25CF" color="#ecc94b" label="Completed outside window" />
          <LegendItem symbol="\u2717" color="#f56565" label="Missed" />
          <LegendItem symbol="\u25CF" color="#4299e1" label="Required (upcoming)" />
          <LegendItem symbol="\u25CB" color="#4a5568" label="Not required" />
        </div>
      </div>

      {/* ================================================================ */}
      {/* SECTION C — Deviation Rules                                     */}
      {/* ================================================================ */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Auto-Generated Deviation Rules</h2>
        <p style={{ color: '#a0aec0', fontSize: 13, margin: '0 0 12px 0' }}>
          Rules derived from the SOA grid above. D3RI automatically flags any evidence event that breaches these thresholds.
        </p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, minWidth: 80 }}>Rule ID</th>
              <th style={{ ...styles.th, minWidth: 90 }}>Severity</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            {DEVIATION_RULES.map((rule) => {
              const sev = SEVERITY_STYLES[rule.severity];
              return (
                <tr key={rule.rule}>
                  <td style={{ ...styles.td, fontFamily: 'monospace', color: '#a0aec0', fontSize: 12 }}>{rule.rule}</td>
                  <td style={styles.td}>
                    <span style={{
                      display: 'inline-block',
                      backgroundColor: sev.bg,
                      color: sev.color,
                      borderRadius: 12,
                      padding: '2px 8px',
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      {rule.severity}
                    </span>
                  </td>
                  <td style={{ ...styles.td, color: '#cbd5e0', fontSize: 13 }}>{rule.description}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ================================================================ */}
      {/* SECTION D — Recording Delay Thresholds (FEATURE 2)              */}
      {/* ================================================================ */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Recording Delay Thresholds</h2>
        <p style={{ color: '#a0aec0', fontSize: 13, margin: '0 0 4px 0' }}>
          Configurable thresholds controlling when a recording delay triggers Green / Amber / Red status.
          Each threshold maps to a specific regulatory requirement.
        </p>
        <p style={{ color: '#63b3ed', fontSize: 12, margin: '0 0 12px 0', fontStyle: 'italic' }}>
          Note: UCLA site SOP mandates a 3-business-day maximum recording delay for all general data entries. This is stricter than the ICH E6(R3) baseline.
        </p>

        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, minWidth: 130 }}>Event Type</th>
              <th style={{ ...styles.th, minWidth: 100, textAlign: 'center' }}>
                <span style={{ color: '#48bb78' }}>Green</span>
              </th>
              <th style={{ ...styles.th, minWidth: 110, textAlign: 'center' }}>
                <span style={{ color: '#ecc94b' }}>Amber</span>
              </th>
              <th style={{ ...styles.th, minWidth: 80, textAlign: 'center' }}>
                <span style={{ color: '#f56565' }}>Red</span>
              </th>
              <th style={{ ...styles.th, minWidth: 200 }}>Regulatory Basis</th>
              <th style={{ ...styles.th, minWidth: 60, textAlign: 'center' }}>Edit</th>
            </tr>
          </thead>
          <tbody>
            {THRESHOLD_ROWS.map((row, idx) => (
              <tr key={row.eventType}>
                <td style={{ ...styles.td, fontWeight: 600, color: '#e2e8f0' }}>{row.eventType}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{ ...styles.thresholdBadge, backgroundColor: '#22543d', color: '#48bb78' }}>{row.green}</span>
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{ ...styles.thresholdBadge, backgroundColor: '#744210', color: '#ecc94b' }}>{row.amber}</span>
                </td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <span style={{ ...styles.thresholdBadge, backgroundColor: '#742a2a', color: '#f56565' }}>{row.red}</span>
                </td>
                <td style={{ ...styles.td, color: '#a0aec0', fontSize: 12 }}>{row.regulatory}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <button
                    onClick={() => setEditingRow(editingRow === idx ? null : idx)}
                    style={{
                      background: editingRow === idx ? '#2b6cb0' : 'transparent',
                      border: `1px solid ${editingRow === idx ? '#2b6cb0' : '#4a5568'}`,
                      borderRadius: 4,
                      color: editingRow === idx ? '#fff' : '#a0aec0',
                      cursor: 'pointer',
                      padding: '2px 10px',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                    title={`Edit thresholds for ${row.eventType}`}
                  >
                    {editingRow === idx ? 'Editing' : 'Edit'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {editingRow !== null && (
          <div style={{
            marginTop: 12,
            padding: '12px 16px',
            backgroundColor: '#2d3748',
            borderRadius: 8,
            border: '1px solid #4a5568',
          }}>
            <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Editing: {THRESHOLD_ROWS[editingRow].eventType}
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <ThresholdInput label="Green \u2264" defaultValue={THRESHOLD_ROWS[editingRow].green} color="#48bb78" />
              <ThresholdInput label="Amber range" defaultValue={THRESHOLD_ROWS[editingRow].amber} color="#ecc94b" />
              <ThresholdInput label="Red >" defaultValue={THRESHOLD_ROWS[editingRow].red} color="#f56565" />
            </div>
            <p style={{ color: '#718096', fontSize: 11, margin: '8px 0 0 0' }}>
              Changes are validated against regulatory minimums before saving. Thresholds cannot be set less strict than the regulatory basis.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
function ConfigItem({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ color: '#a0aec0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</div>
      <div style={{ color: '#e2e8f0', fontSize: 14, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function StatBadge({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{
      backgroundColor: 'rgba(0,0,0,0.3)',
      border: `1px solid ${color}40`,
      borderRadius: 8,
      padding: '8px 16px',
      textAlign: 'center',
      minWidth: 120,
    }}>
      <div style={{ color, fontSize: 20, fontWeight: 700 }}>{value}</div>
      <div style={{ color: '#a0aec0', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>{label}</div>
    </div>
  );
}

function LegendItem({ symbol, color, label }: { symbol: string; color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ color, fontSize: 14, fontWeight: 700 }}>{symbol}</span>
      <span style={{ color: '#a0aec0', fontSize: 12 }}>{label}</span>
    </div>
  );
}

function ThresholdInput({ label, defaultValue, color }: { label: string; defaultValue: string; color: string }) {
  return (
    <div>
      <label style={{ color, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>{label}</label>
      <input
        type="text"
        defaultValue={defaultValue}
        style={{
          backgroundColor: '#1a202c',
          border: '1px solid #4a5568',
          borderRadius: 4,
          color: '#e2e8f0',
          padding: '4px 8px',
          fontSize: 13,
          width: 120,
          outline: 'none',
        }}
        readOnly
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: 32,
    maxWidth: 1400,
    margin: '0 auto',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: '#e2e8f0',
    margin: 0,
  },
  card: {
    backgroundColor: '#1a202c',
    border: '1px solid #2d3748',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: '#e2e8f0',
    margin: '0 0 12px 0',
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
    gap: 8,
  },
  statsRow: {
    display: 'flex',
    gap: 12,
    marginBottom: 16,
    flexWrap: 'wrap' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
    color: '#a0aec0',
    padding: '10px 12px',
    borderBottom: '1px solid #2d3748',
  },
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid #1a365d',
    fontSize: 13,
    color: '#cbd5e0',
  },
  thresholdBadge: {
    display: 'inline-block',
    borderRadius: 12,
    padding: '2px 8px',
    fontSize: 11,
    fontWeight: 600,
  },
};
